import katex from "katex";
import "katex/dist/katex.min.css";

export const FORMULA_SELECTOR = [
  "span[data-formula]",
  "span[data-lh6-formula]",
  "span.lh6-formulaNode",
  "span.lh6-formulaRendered",
  "span.lh6-katexFormula",
  "div[data-formula-block]",
  "div[data-lh6-formula]",
].join(", ");

export function normalizeFormulaLatex(value = "") {
  return String(value || "")
    .replace(/\u00a0/g, " ")
    .replace(/\r\n?/g, "\n")
    .trim();
}

function isDisplayMode(element) {
  const raw =
    element.getAttribute("data-display-mode") ||
    element.getAttribute("data-formula-block") ||
    "0";
  return (
    raw === "1" ||
    raw === "true" ||
    element.tagName?.toLowerCase() === "div" ||
    element.classList?.contains("is-block")
  );
}

function getLatex(element) {
  const direct = element.getAttribute("data-latex") || element.getAttribute("data-value");
  if (direct) return direct;

  const annotation = element.querySelector?.('annotation[encoding="application/x-tex"]');
  if (annotation?.textContent) return annotation.textContent;

  const mathfield = element.querySelector?.("math-field");
  if (mathfield) {
    if (typeof mathfield.value === "string") return mathfield.value;
    if (typeof mathfield.getValue === "function") {
      try {
        return mathfield.getValue("latex") || "";
      } catch {}
      try {
        return mathfield.getValue() || "";
      } catch {}
    }
  }

  return element.textContent || "";
}

export function renderKatexFormulaHtml(latex, displayMode = false) {
  return katex.renderToString(latex, {
    throwOnError: false,
    strict: "ignore",
    trust: false,
    output: "htmlAndMathml",
    displayMode,
  });
}

export function normalizeFormulasForPersistence(innerHtml) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${innerHtml || ""}</div>`, "text/html");
  const wrapper = doc.body.firstElementChild;
  if (!wrapper) return innerHtml || "";

  wrapper.querySelectorAll(FORMULA_SELECTOR).forEach((element) => {
    const latex = normalizeFormulaLatex(getLatex(element));
    if (!latex) {
      element.remove();
      return;
    }

    const displayMode = isDisplayMode(element);
    const normalized = doc.createElement("span");
    normalized.className = `lh6-formulaNode lh6-katexFormula ${displayMode ? "is-block" : "is-inline"}`;
    normalized.setAttribute("data-formula", "1");
    normalized.setAttribute("data-lh6-formula", "1");
    normalized.setAttribute("data-latex", latex);
    normalized.setAttribute("data-display-mode", displayMode ? "1" : "0");
    normalized.textContent = latex;
    element.replaceWith(normalized);
  });

  return wrapper.innerHTML;
}

export function renderSavedFormulasIn(rootElement) {
  if (!rootElement || typeof rootElement.querySelectorAll !== "function") return;

  const formulaElements = rootElement.matches?.(FORMULA_SELECTOR)
    ? [rootElement]
    : Array.from(rootElement.querySelectorAll(FORMULA_SELECTOR));

  formulaElements.forEach((element) => {
    if (!element || element.querySelector?.("math-field")) return;

    const latex = normalizeFormulaLatex(getLatex(element));
    if (!latex) {
      element.textContent = "";
      return;
    }

    const displayMode = isDisplayMode(element);
    const renderedLatex = element.getAttribute("data-rendered-latex") || "";
    const renderedDisplayMode = element.getAttribute("data-rendered-display-mode") || "";
    if (
      renderedLatex === latex &&
      renderedDisplayMode === (displayMode ? "1" : "0") &&
      element.querySelector?.(".katex")
    ) {
      return;
    }

    try {
      const html = renderKatexFormulaHtml(latex, displayMode);

      element.innerHTML = html;
      element.setAttribute("data-latex", latex);
      element.setAttribute("data-formula", "1");
      element.setAttribute("data-lh6-formula", "1");
      element.setAttribute("data-display-mode", displayMode ? "1" : "0");
      element.classList.add("lh6-formulaRendered");
      element.classList.add("lh6-katexFormula");
      element.classList.toggle("is-block", displayMode);
      element.classList.toggle("is-inline", !displayMode);
      element.setAttribute("role", "math");
      element.setAttribute("aria-label", latex);
      element.setAttribute("data-rendered-latex", latex);
      element.setAttribute("data-rendered-display-mode", displayMode ? "1" : "0");
    } catch {
      element.textContent = latex;
      element.classList.add("lh6-formulaRendered", "lh6-katexFormula");
      element.classList.toggle("is-block", displayMode);
      element.classList.toggle("is-inline", !displayMode);
    }
  });
}

export function installSavedFormulaRenderer(rootElement) {
  if (!rootElement || typeof rootElement.querySelectorAll !== "function") {
    return () => {};
  }

  renderSavedFormulasIn(rootElement);

  if (typeof MutationObserver === "undefined") {
    return () => {};
  }

  let queued = false;
  const queueRender = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      renderSavedFormulasIn(rootElement);
    });
  };

  const observer = new MutationObserver((mutations) => {
    const shouldRender = mutations.some((mutation) => {
      if (mutation.type === "childList") return true;
      if (mutation.type !== "attributes") return false;
      return ["class", "data-latex", "data-value", "data-display-mode", "data-formula-block"].includes(
        mutation.attributeName
      );
    });

    if (shouldRender) queueRender();
  });

  observer.observe(rootElement, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ["class", "data-latex", "data-value", "data-display-mode", "data-formula-block"],
  });

  return () => observer.disconnect();
}

export default renderSavedFormulasIn;
